import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Input,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import {
    IOwnershipArea,
    IOwnershipBusinessUnit,
    IOwnershipUser,
    ITransferOwnershipData,
} from '@fi-app/other-settings/transfer-ownership/fi-transfer-ownership';
import { FiTransferOwnershipService } from '@fi-app/other-settings/transfer-ownership/fi-transfer-ownership.service';
import { ObjectTypes } from '@fi-shared/types';
import { isDefined } from '@fi-shared/utils';
import { FiScrollToElementAnimateService } from '@fi-shared/fi-shared-services/scroll-to-element-animate.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';

@Component({
    selector: 'fi-transfer-ownership',
    templateUrl: './fi-transfer-ownership.component.html',
    styleUrl: './fi-transfer-ownership.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-transfer-ownership' },
    standalone: false,
})
export class FiTransferOwnershipComponent implements OnInit {
    @Input() sourceUserId: number;
    transferOwnershipData: ITransferOwnershipData = undefined;
    loading$ = new BehaviorSubject(true);
    sourceUser: IOwnershipUser;
    targetUser: IOwnershipUser;
    selectedObjectTypes: ObjectTypes[];
    resetSummary$ = new Subject<void>();
    backButtonTitleTextId$ = new BehaviorSubject('common.cancel');
    isTransferOwnershipCompleted$ = new BehaviorSubject<boolean>(false);

    @ViewChild('fiContentEl') fiContentEl: ElementRef;
    @ViewChild('ownershipCompletedCardWrapper') set ownershipCompletedCardWrapper(value: ElementRef) {
        if (isDefined(value))
            this.scrollToElementAnimateService.scrollTo(value.nativeElement, this.fiContentEl.nativeElement);
    }

    constructor(
        private fiTransferOwnershipService: FiTransferOwnershipService,
        private scrollToElementAnimateService: FiScrollToElementAnimateService,
        private fiStateService: FiStateService,
    ) {}

    ngOnInit(): void {
        this.getTransferOwnershipData();
    }

    private getTransferOwnershipData() {
        this.loading$.next(true);
        this.fiTransferOwnershipService
            .getTransferOwnershipData()
            .then((result: ITransferOwnershipData) => {
                this.transferOwnershipData = result;
                this.setSourceUserData();
            })
            .finally(() => this.loading$.next(false));
    }

    private setSourceUserData() {
        if (!this.sourceUserId) return;

        this.sourceUser = this.transferOwnershipData.FromAreas.flatMap((area: IOwnershipArea) => area.BusinessUnits)
            .flatMap((unit: IOwnershipBusinessUnit) => unit.Users)
            .find((user: IOwnershipUser) => user.Id === this.sourceUserId);
        this.sourceUserId = undefined;
    }

    resetSummary() {
        this.resetSummary$.next();
    }

    getSourceUserAreaName(): string {
        return this.transferOwnershipData.FromAreas.find((area: IOwnershipArea) => area.Id === this.sourceUser?.AreaId)
            ?.Name;
    }

    getTargetUserAreaName(): string {
        return this.transferOwnershipData.ToAreas.find((area: IOwnershipArea) => area.Id === this.targetUser?.AreaId)
            ?.Name;
    }

    isShowSummaryButtonDisabled(): boolean {
        return !this.selectedObjectTypes?.length;
    }

    transferOwnership() {
        this.fiTransferOwnershipService
            .transferOwnerShip(this.sourceUser.Id, this.targetUser.Id, this.selectedObjectTypes)
            .then(() => {
                this.isTransferOwnershipCompleted$.next(true);
                this.backButtonTitleTextId$.next('common.close');
            });
    }

    navigateToSettingsPage() {
        this.fiStateService.go(FiStateNames.OtherSettings.List);
    }

    protected readonly isDefined = isDefined;
}
